package modysy.tools

import modysy.datastructures.Vector2
import modysy.view.Color
import org.scalajs.dom.CanvasRenderingContext2D
import scala.scalajs.js
import scala.collection.mutable.ArrayBuffer
import org.scalajs.dom

/** Displays a Dialog Box at a given position with given text.
  * An anchor can be set to render the dialog box like a speech bubble.
  *
  * @param pos      is a Vector2 that defines the center of the dialog box
  * @param text     defines the text that is shown in the dialog
  * @param width    defines the max width of the dialog box (height is calculated
  *                 automatically)
  * @param cooldown defines a cooldown in ms for the dialog box
  * @param anchor   can be "left", "right", "top" or "bottom" and defines an
  *                 anchor point for the object, where the dialog box is related to. When an
  *                 anchor is set, the dialog box looks like a speech bubble.
  */
class Dialog(
    var pos: Vector2,
    var text: String,
    var width: Double,
    var cooldown: Double,
    var anchor: String = "none"
) {

  private var active: Boolean = false
  private var ini: Boolean = true
  var startTime = 0
  private val radius: Double = 10
  private val maxWidth: Double = width + radius
  private var height: Double = 0
  private var words = text.split(" ")
  private var lines = ArrayBuffer[String]()
  private var line = ""
  // cooldown
  private var t0: Double = 0
  private var t1: Double = 0
  private var cdstart: Double = 0

  /**
    * Method for rendering the dialog box.
    *
    * @param ctx is the rendering context of the canvas to rener on.
    */
  def render(ctx: CanvasRenderingContext2D): Unit = {
    if (ini) {
      ctx.font = "20px Arial"
      for (word <- words) {
        if (ctx.measureText(line + word + " ").width > maxWidth) {
          lines += line
          line = word + " "
        } else {
          line += word + " "
        }
      }
      lines += line
      height = (lines.length * 20)
      ini = !ini
      active = !active
      if (cooldown > 0) {
        t0 = js.Date.now()
      }
      cdstart = cooldown
    }

    //rendering dialog box
    if (active) {
      ctx.save()
      ctx.fillStyle = Color.White
      ctx.strokeStyle = Color.Black
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(
        pos.x - width * 0.5,
        pos.y - height * 0.5,
        radius,
        Math.PI,
        Math.PI * 1.5
      )
      if (anchor == "top") {
        ctx.lineTo(pos.x - width * 0.2, pos.y - height * 0.5 - radius)
        ctx.lineTo(pos.x, pos.y - height * 0.7 - radius)
        ctx.lineTo(pos.x + width * 0.2, pos.y - height * 0.5 - radius)
        ctx.lineTo(pos.x + width * 0.5, pos.y - height * 0.5 - radius)
      }
      ctx.arc(
        pos.x + width * 0.5,
        pos.y - height * 0.5,
        radius,
        Math.PI * 1.5,
        0
      )
      if (anchor == "right") {
        ctx.lineTo(pos.x + width * 0.5 + radius, pos.y - height * 0.2)
        ctx.lineTo(pos.x + width * 0.7 + radius, pos.y)
        ctx.lineTo(pos.x + width * 0.5 + radius, pos.y + height * 0.2)
        ctx.lineTo(pos.x + width * 0.5 + radius, pos.y + height * 0.5)
      }
      ctx.arc(
        pos.x + width * 0.5,
        pos.y + height * 0.5,
        radius,
        0,
        Math.PI * 0.5
      )
      if (anchor == "bottom") {
        ctx.lineTo(pos.x + width * 0.2, pos.y + height * 0.5 + radius)
        ctx.lineTo(pos.x, pos.y + height * 0.7 + radius)
        ctx.lineTo(pos.x - width * 0.2, pos.y + height * 0.5 + radius)
        ctx.lineTo(pos.x - width * 0.5, pos.y + height * 0.5 + radius)
      }
      ctx.arc(
        pos.x - width * 0.5,
        pos.y + height * 0.5,
        radius,
        Math.PI * 0.5,
        Math.PI
      )
      if (anchor == "left") {
        ctx.lineTo(pos.x - width * 0.5 - radius, pos.y + height * 0.2)
        ctx.lineTo(pos.x - width * 0.7 - radius, pos.y)
        ctx.lineTo(pos.x - width * 0.5 - radius, pos.y - height * 0.2)
      }
      ctx.closePath()
      ctx.fill()
      ctx.stroke()

      //rendering of Text
      var linex = pos.x - width * 0.5
      var liney = pos.y - height * 0.5 + radius

      ctx.font = "20px Arial"
      ctx.textAlign = "left"
      ctx.fillStyle = Color.Black
      for (line <- lines) {
        ctx.fillText(line, linex, liney, Integer.MAX_VALUE)
        liney += 20
      }

      ctx.restore()

      if (cooldown > 0) {
        t1 = js.Date.now()
        cooldown = cdstart - (t1 - t0)
      } else {
        if (cdstart > 0) {
          active = !active
        }
      }

    }

  }

  def primaryMouseButtonPressed(
      mousePosScreen: Vector2, // TODO actually a canvas position
      mousePosWorld: Vector2
  ): Unit = { // TODO ?¿ what is happening here ¿?
    var posx = mousePosScreen.x
    var posy = mousePosScreen.y
    var w = width + 2 * radius
    var h = height + 2 * radius
    var L = pos.x - width * 0.5 - radius
    var R = pos.x + width * 0.5 + radius
    var T = pos.y - height * 0.5 - radius
    var B = pos.y + height * 0.5 + radius

    if ((posx >= L && posx <= R) && (posy >= T && posy <= B)) {
      active = false
    }
  }

  /** Resets the dialog box.
    *
    * @param cd defines a new cooldown for the dialog box. (negative cooldown
    *           = dialog box doesn't disappear)
    **/
  def reset(cd: Double = -1): Unit = {
    active = false
    ini = true
    cooldown = cd
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/Dialog.scala